import { useCallback, useState } from "react";
import { useMutation, useQuery } from "urql";
import type {
  BulkSendUnbookedManualReminderMutation as BulkSendUnbookedManualReminderMutationResult,
  StakeholderRespondentsQuery as StakeholderRespondentsQueryResult,
} from "../graphql/generated/graphql";
import { CandidateStatus } from "../graphql/generated/graphql";
import {
  BulkSendUnbookedManualReminderMutation,
  StakeholderRespondentsQuery,
} from "../graphql/stakeholderSettings.graphql";
import { toUserFacingMessage } from "../lib/graphqlErrorMessage";

/** 回答者1名（= インタビュー1回）。スキーマとずれないよう生成型から導出する */
export type StakeholderRespondent =
  StakeholderRespondentsQueryResult["stakeholderRespondents"][number];

/**
 * 依頼を送信できる回答者かどうか。
 *
 * 未送信であることに加えて、受け取る人が1人以上いることを見る。受け取る人が空のまま送っても
 * backend が弾くので、ボタンの活性と一括送信の件数を送信できる人だけに揃える。
 */
export function isSendableRespondent(
  respondent: StakeholderRespondent,
): boolean {
  return (
    respondent.status === CandidateStatus.Unsent &&
    respondent.recipientNames.length > 0
  );
}

/**
 * 未予約のためリマインドを送る対象かどうか。
 *
 * 送信済みで予約が入っていない回答者だけを数える。未送信の人は依頼そのものが届いていないので、
 * 催促ではなく依頼の送信が先になる。
 */
export function isRemindableRespondent(
  respondent: StakeholderRespondent,
): boolean {
  return respondent.status === CandidateStatus.Sent;
}

const JST_OFFSET_MS = 9 * 60 * 60 * 1000;
const MS_PER_DAY = 86_400_000;

/** JST の暦日を表す通し番号。閲覧している端末のタイムゾーンに依存させない */
function jstDayNumber(time: number): number {
  return Math.floor((time + JST_OFFSET_MS) / MS_PER_DAY);
}

/**
 * 依頼からの経過日数。依頼日が無い回答者は null。
 *
 * 経過時間ではなく JST の暦日の差で数える。運用上知りたいのは「何日放置されているか」で、
 * 24時間未満でも日付が変われば1日と数えるほうが感覚に合う。
 */
export function elapsedDaysSinceRequest(
  requestedAt: string | null,
  now: Date = new Date(),
): number | null {
  if (!requestedAt) return null;
  const requested = new Date(requestedAt);
  if (Number.isNaN(requested.getTime())) return null;
  return Math.max(
    0,
    jstDayNumber(now.getTime()) - jstDayNumber(requested.getTime()),
  );
}

export function useStakeholderRespondents(termId: string | null) {
  const [result, reexecuteQuery] = useQuery({
    query: StakeholderRespondentsQuery,
    variables: { termId: termId ?? "" },
    pause: !termId,
  });

  const refetch = useCallback(() => {
    reexecuteQuery({ requestPolicy: "network-only" });
  }, [reexecuteQuery]);

  return {
    stakeholderRespondents: result.data?.stakeholderRespondents ?? [],
    loading: result.fetching,
    error: result.error
      ? toUserFacingMessage(result.error, "回答者の取得に失敗しました")
      : null,
    refetch,
  };
}

/** 一括リマインドの結果。スキーマとずれないよう生成型から導出する */
export type BulkSendUnbookedManualReminderResult = NonNullable<
  BulkSendUnbookedManualReminderMutationResult["bulkSendUnbookedManualReminder"]
>;

/**
 * 一括リマインドの実行結果。
 *
 * 失敗理由を hook の state に入れて null を返す形にすると、呼び出し側はクリック時点の
 * レンダーに束縛された古い値を読んでしまう（初回は必ず空、2回目以降は前回の理由）。
 * 理由は戻り値で渡す。
 */
export type BulkSendUnbookedManualReminderOutcome =
  | { ok: true; result: BulkSendUnbookedManualReminderResult }
  | { ok: false; message: string };

export function useUnbookedManualReminder() {
  const [, executeBulkSendUnbookedManualReminder] = useMutation(
    BulkSendUnbookedManualReminderMutation,
  );
  const [loading, setLoading] = useState(false);

  const bulkSendUnbookedManualReminder = useCallback(
    async (termId: string): Promise<BulkSendUnbookedManualReminderOutcome> => {
      setLoading(true);
      try {
        const response = await executeBulkSendUnbookedManualReminder({
          termId,
        });
        if (response.error) {
          return {
            ok: false,
            message: toUserFacingMessage(
              response.error,
              "リマインドの送信に失敗しました。時間をおいてから再度お試しください",
            ),
          };
        }
        const result = response.data?.bulkSendUnbookedManualReminder;
        if (!result) {
          return {
            ok: false,
            message:
              "リマインドの送信に失敗しました。時間をおいてから再度お試しください",
          };
        }
        return { ok: true, result };
      } finally {
        setLoading(false);
      }
    },
    [executeBulkSendUnbookedManualReminder],
  );

  return { bulkSendUnbookedManualReminder, loading };
}
